import {
  ConflictLevelsStatusEnum,
  type TaskFilterOptionDto,
} from '@sr-sdks/api-sdk-axios';
import { isValid, parseISO } from 'date-fns';
import { MAX_LIST_VALUES, isClientIdValue } from '../Title/titleFilterOptions';
import type { TitleFilter } from '../Title/useTitleFilters';

export type TaskFilterKey = 'client' | 'dueDate' | 'otherClaimant' | 'status';

export const TASKS_PAGE_PARAM = 'page';

export const TASK_FILTER_PARAMS = {
  client: 'tasksClient',
  dueDate: 'tasksDueDate',
  otherClaimant: 'tasksOtherClaimant',
  status: 'tasksStatus',
} as const satisfies Record<TaskFilterKey, string>;

export const UNKNOWN_CLAIMANT_LABEL = 'Unknown claimant';

/** Clients the backend could name, by id; an unlabelled one is left to a client lookup. */
export const optionClientNames = (clients: TaskFilterOptionDto[] = []) =>
  new Map(
    clients.flatMap(({ label, value }) =>
      label ? [[value, label] as const] : [],
    ),
  );

/** Other claimants by key, with `unknownLabel` for one the backend couldn't name. */
export const optionOtherClaimantNames = (
  otherClaimants: TaskFilterOptionDto[] = [],
  unknownLabel: string,
) =>
  new Map(
    otherClaimants.map(({ label, value }) => [value, label || unknownLabel]),
  );

/**
 * Merges a workspace's same-name options, so lookalikes can't select different
 * claims. `keysByValue` lists every key each merged option stands for.
 */
export const mergeOtherClaimantOptions = (
  otherClaimants: TaskFilterOptionDto[] = [],
) => {
  const options: TaskFilterOptionDto[] = [];
  const keysByValue = new Map<string, string[]>();
  const firstValueByGroup = new Map<string, string>();

  for (const option of otherClaimants) {
    const { label, value } = option;
    const [otherWorkspaceId] = value.split('.');
    // An unnamed option is a different party each time, so it never joins a group.
    const group = label ? JSON.stringify([otherWorkspaceId, label]) : undefined;
    const firstValue = group ? firstValueByGroup.get(group) : undefined;

    if (firstValue !== undefined) {
      keysByValue.get(firstValue)?.push(value);
      continue;
    }

    options.push(option);
    keysByValue.set(value, [value]);

    if (group) firstValueByGroup.set(group, value);
  }

  return { keysByValue, options };
};

/**
 * Values with all their keys today, whole groups only, in the order given, up to the
 * list's cap: one that would overflow is left out, so it can't apply in part or 400.
 */
export const fitClaimantValues = (
  values: string[],
  keysByValue: Map<string, string[]>,
  maxKeys = MAX_LIST_VALUES,
) => {
  const fitted: string[] = [];
  const keys = new Set<string>();

  for (const value of new Set(values)) {
    const added = (keysByValue.get(value) ?? [value]).filter(
      (key) => !keys.has(key),
    );

    if (keys.size + added.length > maxKeys) continue;

    fitted.push(value);
    added.forEach((key) => keys.add(key));
  }

  return { keys: [...keys], values: fitted };
};

/**
 * The URL filter with each merged group as one value, ticked when any of its keys
 * is and it fits the cap; a stale key outside every group passes through.
 */
export const toMergedClaimantFilter = (
  filter: TitleFilter,
  keysByValue: Map<string, string[]>,
): TitleFilter => {
  const valueByKey = new Map(
    [...keysByValue].flatMap(([value, keys]) =>
      keys.map((key) => [key, value] as const),
    ),
  );

  const selected = fitClaimantValues(
    filter.selected.map((key) => valueByKey.get(key) ?? key),
    keysByValue,
  ).values;

  return {
    selected,
    // The combobox sorts picks by key, so ticked groups go first and a new pick is what's left out.
    setSelected: (values) =>
      filter.setSelected(
        fitClaimantValues(
          [
            ...values.filter((value) => selected.includes(value)),
            ...values.filter((value) => !selected.includes(value)),
          ],
          keysByValue,
        ).keys,
      ),
  };
};

/**
 * Every competing-claim status, not only those on today's tasks — `Resolved`
 * included — sorted A→Z so a long list is easy to scan.
 */
export const TASK_STATUS_OPTIONS = Object.values(ConflictLevelsStatusEnum)
  .sort((a, b) => a.localeCompare(b))
  .map((status) => ({ label: status, value: status }));

export const isTaskStatusValue = (
  value: string,
): value is ConflictLevelsStatusEnum =>
  (Object.values(ConflictLevelsStatusEnum) as string[]).includes(value);

/**
 * Matches the backend's `YYYY-MM-DD` rule, so a hand-edited URL can't 400 the page,
 * and rejects impossible days like `2026-02-31`, which would crash the date label.
 */
export const isCalendarDateValue = (value: string): value is string =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value));

/** Matches the backend's `otherWorkspaceId[.[otherClientId]]` key, so a hand-edited URL can't 400 the page. */
export const isOtherClaimantValue = (value: string): value is string => {
  const [otherWorkspaceId, otherClientId, ...rest] = value.split('.');

  return (
    !rest.length &&
    isClientIdValue(otherWorkspaceId) &&
    (!otherClientId || isClientIdValue(otherClientId))
  );
};
